// Opens a preview of the cache page in a new tab, sized to the device the page was built
// for. The page is embedded in a fixed size iframe rather than just resizing the window,
// so the cached page sees a real viewport of that width and its media queries respond as
// they would on a device.
//
// pg.device comes back from /steps/reusablecachepg as "Mobile" / "Desktop" / "Responsive".
// A responsive page gets a device options modal in the popped out tab so you can flick
// between the two sizes; a fixed one just opens at its own size.
const DEVICE_SIZES = {
  mobile: { width: 390, height: 844, label: "Mobile" },
  desktop: { width: 1440, height: 900, label: "Desktop" },
};

// anything we don't recognise (including a null device) is treated as responsive - that
// way the user gets to choose rather than us guessing a size that might be wrong
export function normaliseDevice(device) {
  const value = String(device || "").trim().toLowerCase();

  if (value === "mobile") {
    return "mobile";
  }

  if (value === "desktop") {
    return "desktop";
  }

  return "responsive";
}

// values are interpolated into the preview markup, so escape them. & has to go first or it
// would double escape the entities added after it - the url carries ?step_id= and may well
// pick up further &params later.
function escapeHtml(value) {
  return String(value)
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

function sizeCaption(size) {
  return `${size.label} &mdash; ${size.width} &times; ${size.height}`;
}

function deviceOptionsMarkup() {
  return `
    <button class="fab" id="openOptions" type="button">Device options</button>

    <div class="backdrop" id="backdrop" hidden>
      <div class="modal" role="dialog" aria-modal="true" aria-label="Choose a device">
        <h2>Preview as</h2>
        <button class="choice" type="button" data-view="mobile">${sizeCaption(DEVICE_SIZES.mobile)}</button>
        <button class="choice" type="button" data-view="desktop">${sizeCaption(DEVICE_SIZES.desktop)}</button>
        <button class="close" id="closeOptions" type="button">Close</button>
      </div>
    </div>

    <script>
      (function () {
        var sizes = ${JSON.stringify(DEVICE_SIZES)};
        var frame = document.getElementById('device');
        var caption = document.getElementById('caption');
        var backdrop = document.getElementById('backdrop');

        function apply(view) {
          var size = sizes[view];
          if (!size) { return; }
          frame.style.width = size.width + 'px';
          frame.style.height = size.height + 'px';
          frame.className = 'device ' + view;
          caption.textContent = size.label + ' \\u2014 ' + size.width + ' \\u00d7 ' + size.height;
        }

        function setOpen(open) { backdrop.hidden = !open; }

        document.getElementById('openOptions').addEventListener('click', function () { setOpen(true); });
        document.getElementById('closeOptions').addEventListener('click', function () { setOpen(false); });

        // click outside the panel closes it
        backdrop.addEventListener('click', function (event) {
          if (event.target === backdrop) { setOpen(false); }
        });

        document.addEventListener('keydown', function (event) {
          if (event.key === 'Escape') { setOpen(false); }
        });

        Array.prototype.forEach.call(
          document.querySelectorAll('.modal button[data-view]'),
          function (button) {
            button.addEventListener('click', function () {
              apply(button.getAttribute('data-view'));
              setOpen(false);
            });
          }
        );
      })();
    </script>`;
}

export function previewDocument(src, device) {
  const view = normaliseDevice(device);
  const isResponsive = view === "responsive";
  // a responsive page has to start somewhere - open on mobile and let the modal switch it
  const startView = isResponsive ? "mobile" : view;
  const size = DEVICE_SIZES[startView];
  const url = escapeHtml(src);

  return `<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1" />
    <title>Cache page preview</title>
    <style>
      * { box-sizing: border-box; }
      body {
        margin: 0;
        min-height: 100vh;
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 16px;
        padding: 24px 16px;
        background: #212b36;
        color: #f4f6f8;
        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
      }
      header { text-align: center; max-width: 100%; }
      h1 { margin: 0 0 4px; font-size: 16px; font-weight: 600; }
      p { margin: 0; font-size: 12px; color: #919eab; max-width: 640px; }
      p.url { word-break: break-all; margin-top: 4px; }
      /* a desktop frame is wider than most screens, so let the stage scroll */
      .stage { width: 100%; overflow: auto; display: flex; justify-content: center; }
      .device {
        width: ${size.width}px;
        height: ${size.height}px;
        flex-shrink: 0;
        border: 10px solid #161c24;
        border-radius: 36px;
        overflow: hidden;
        background: #fff;
        box-shadow: 0 12px 32px rgba(0, 0, 0, 0.45);
      }
      /* only a phone gets the rounded bezel */
      .device.desktop { border-radius: 8px; border-width: 8px; }
      iframe { width: 100%; height: 100%; border: 0; display: block; }
      .fab {
        position: fixed;
        top: 16px;
        right: 16px;
        padding: 8px 14px;
        border: 0;
        border-radius: 6px;
        background: #00ab55;
        color: #fff;
        font-size: 13px;
        font-weight: 600;
        cursor: pointer;
      }
      .backdrop {
        position: fixed;
        inset: 0;
        background: rgba(0, 0, 0, 0.6);
        display: flex;
        align-items: center;
        justify-content: center;
      }
      .backdrop[hidden] { display: none; }
      .modal {
        background: #fff;
        color: #212b36;
        border-radius: 10px;
        padding: 20px;
        min-width: 260px;
        display: flex;
        flex-direction: column;
        gap: 10px;
        box-shadow: 0 16px 40px rgba(0, 0, 0, 0.4);
      }
      .modal h2 { margin: 0 0 4px; font-size: 15px; }
      .modal button {
        padding: 10px 12px;
        border-radius: 6px;
        border: 1px solid #dfe3e8;
        background: #f4f6f8;
        font-size: 13px;
        cursor: pointer;
      }
      .modal button.close { border: 0; background: transparent; color: #637381; }
    </style>
  </head>
  <body>
    <header>
      <h1>Cache page preview</h1>
      <p id="caption">${sizeCaption(size)}</p>
      <p class="url">${url}</p>
    </header>

    <div class="stage">
      <div class="device ${startView}" id="device">
        <iframe src="${url}" title="Cache page preview"></iframe>
      </div>
    </div>
${isResponsive ? deviceOptionsMarkup() : ""}
  </body>
</html>`;
}

export default function openMobilePreview(src, device) {
  if (!src) {
    return null;
  }

  const tab = window.open("", "_blank");

  // blocked by the popup blocker
  if (!tab) {
    return null;
  }

  tab.document.write(previewDocument(src, device));
  tab.document.close();

  return tab;
}
